<template>
  <main class="payment-settings-page">
    <header>
      <div>
        <h1>支付设置</h1>
        <span>微信、支付宝和第三方聚合支付均在这里维护；商户密钥加密保存且不会回显。</span>
      </div>
      <a-button :loading="loading" @click="load"><i class="ri-refresh-line" />刷新</a-button>
    </header>
    <a-alert
      type="info"
      show-icon
      message="填写凭据、设置回调地址并开启渠道，即可立即提供支付服务。"
      description="密钥和私钥仅在本次保存时提交；已保存的密钥留空即可保留。关闭渠道不影响已支付订单的退款处理。"
    />
    <a-spin :spinning="loading">
      <a-tabs v-model:active-key="activeTab" class="payment-tabs">
        <a-tab-pane key="wechat" tab="微信支付 · Native 扫码">
          <a-card title="微信支付渠道"
            ><template #extra
              ><a-badge :status="credentialBadge(wechat)" :text="credentialText(wechat)"
            /></template>
            <a-form layout="vertical">
              <ChannelPolicyForm
                :channel="wechat"
                :product-kind-options="productKindOptions"
                open-label="开放微信支付"
              />
              <a-divider>商户连接</a-divider>
              <div class="form-row">
                <a-form-item label="AppID"
                  ><a-input v-model:value="credentials.wechatNative.appId" /></a-form-item
                ><a-form-item label="商户号"
                  ><a-input v-model:value="credentials.wechatNative.mchId"
                /></a-form-item>
              </div>
              <a-form-item label="商户证书序列号"
                ><a-input v-model:value="credentials.wechatNative.serialNo"
              /></a-form-item>
              <a-form-item label="商户私钥"
                ><a-textarea
                  v-model:value="credentials.wechatNative.privateKey"
                  :rows="3"
                  :placeholder="
                    secretHint(credentials.wechatNative.hasPrivateKey, '粘贴 PEM 私钥')
                  "
              /></a-form-item>
              <a-form-item label="平台证书公钥"
                ><a-textarea
                  v-model:value="credentials.wechatNative.publicKey"
                  :rows="3"
                  :placeholder="secretHint(credentials.wechatNative.hasPublicKey, '粘贴 PEM 公钥')"
              /></a-form-item>
              <a-form-item label="API v3 密钥"
                ><a-input-password
                  v-model:value="credentials.wechatNative.apiV3Key"
                  :placeholder="
                    secretHint(credentials.wechatNative.hasApiV3Key, '填写 API v3 密钥')
                  "
              /></a-form-item>
            </a-form>
            <a-divider>系统回调地址</a-divider
            ><PaymentSystemEndpoints
              v-if="systemEndpoints"
              :items="[{ label: '微信支付回调', value: systemEndpoints.wechatNotifyUrl }]"
            />
          </a-card>
        </a-tab-pane>
        <a-tab-pane key="alipay" tab="支付宝 · 电脑网站支付">
          <a-card title="支付宝渠道"
            ><template #extra
              ><a-badge :status="credentialBadge(alipay)" :text="credentialText(alipay)"
            /></template>
            <a-form layout="vertical">
              <ChannelPolicyForm
                :channel="alipay"
                :product-kind-options="productKindOptions"
                open-label="开放支付宝"
              />
              <a-divider>商户连接</a-divider>
              <a-form-item label="应用 AppID"
                ><a-input v-model:value="credentials.alipayPage.appId"
              /></a-form-item>
              <a-form-item label="应用私钥"
                ><a-textarea
                  v-model:value="credentials.alipayPage.privateKey"
                  :rows="3"
                  :placeholder="secretHint(credentials.alipayPage.hasPrivateKey, '粘贴应用私钥')"
              /></a-form-item>
              <a-form-item label="支付宝公钥"
                ><a-textarea
                  v-model:value="credentials.alipayPage.publicKey"
                  :rows="3"
                  :placeholder="secretHint(credentials.alipayPage.hasPublicKey, '粘贴支付宝公钥')"
              /></a-form-item>
            </a-form>
            <a-divider>系统回调与跳转</a-divider
            ><PaymentSystemEndpoints
              v-if="systemEndpoints"
              :items="[
                { label: '支付宝异步通知', value: systemEndpoints.alipayNotifyUrl },
                { label: '支付完成跳转', value: systemEndpoints.paymentResultUrl },
              ]"
            />
          </a-card>
        </a-tab-pane>
        <a-tab-pane key="uuw" tab="优优聚合支付 · 托管收银台">
          <a-card title="优优聚合支付渠道"
            ><template #extra
              ><a-badge :status="credentialBadge(uuw)" :text="credentialText(uuw)"
            /></template>
            <a-alert
              type="info"
              show-icon
              message="用户会跳转至优优聚合支付收银台。"
              description="仅选择商户后台已开通的支付方式。"
            />
            <a-form layout="vertical" style="margin-top: 16px">
              <ChannelPolicyForm
                :channel="uuw"
                :product-kind-options="productKindOptions"
                open-label="开放优优聚合支付"
                :payment-type-options="uuwPaymentTypeOptions"
              />
              <a-divider>商户连接</a-divider>
              <div class="form-row">
                <a-form-item label="接口地址"
                  ><a-input
                    v-model:value="credentials.uuwGateway.apiBaseUrl"
                    placeholder="https://pay.uuw.cn" /></a-form-item
                ><a-form-item label="商户号（PID）"
                  ><a-input v-model:value="credentials.uuwGateway.pid"
                /></a-form-item>
              </div>
              <a-form-item label="商户密钥"
                ><a-input-password
                  v-model:value="credentials.uuwGateway.key"
                  :placeholder="secretHint(credentials.uuwGateway.hasKey, '填写优优商户密钥')"
              /></a-form-item>
            </a-form>
            <a-divider>系统回调与跳转</a-divider
            ><PaymentSystemEndpoints
              v-if="systemEndpoints"
              :items="[
                { label: '优优异步通知', value: systemEndpoints.uuwNotifyUrl },
                { label: '支付完成跳转', value: systemEndpoints.paymentResultUrl },
              ]"
            />
          </a-card>
        </a-tab-pane>
        <a-tab-pane key="rules" tab="交易与退款规则">
          <div class="rules-grid">
            <a-card title="收银台与订单"
              ><a-form layout="vertical"
                ><a-form-item label="订单支付有效期（分钟）" extra="超时后待支付订单会自动关闭。"
                  ><a-input-number
                    v-model:value="checkout.orderTimeoutMinutes"
                    :min="5"
                    :max="1440"
                    :precision="0" /></a-form-item
                ><a-form-item label="单用户同一商品待支付订单上限"
                  ><a-input-number
                    v-model:value="checkout.pendingOrderLimit"
                    :min="1"
                    :max="20"
                    :precision="0" /></a-form-item
                ><a-form-item label="客服或售后链接"
                  ><a-input
                    v-model:value="checkout.customerServiceUrl"
                    :maxlength="500"
                    placeholder="https://..." /></a-form-item></a-form></a-card
            ><a-card title="退款与售后"
              ><a-form layout="vertical"
                ><a-form-item
                  label="标准退款期限（天）"
                  extra="退款资格以订单支付时间为准；最终退款仍由人工审核。"
                  ><a-input-number
                    v-model:value="refunds.windowDays"
                    :min="0"
                    :max="365"
                    :precision="0" /></a-form-item
                ><a-form-item label="退款失败重试次数"
                  ><a-input-number
                    v-model:value="refunds.retryLimit"
                    :min="0"
                    :max="10"
                    :precision="0" /></a-form-item></a-form
            ></a-card>
          </div>
        </a-tab-pane>
      </a-tabs>
      <a-card title="保存本次调整" class="save-card"
        ><a-form layout="vertical"
          ><a-form-item label="变更原因" required extra="说明会写入管理员审计记录。"
            ><a-input
              v-model:value="reason"
              :maxlength="200"
              show-count
              placeholder="例如：完成生产商户凭据校验后开放微信支付" /></a-form-item
          ><a-space
            ><a-button type="primary" :loading="saving" @click="save">保存并立即生效</a-button
            ><a-button :disabled="saving" @click="load">放弃未保存修改</a-button
            ><span v-if="version" class="version">当前配置版本 v{{ version }}</span></a-space
          ></a-form
        ></a-card
      >
    </a-spin>
  </main>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue';
import { message } from 'ant-design-vue';
import { adminApi } from '@/api';
import ChannelPolicyForm from '@/components/commerce/PaymentChannelPolicyForm.vue';
import PaymentSystemEndpoints from '@/components/commerce/PaymentSystemEndpoints.vue';
import { productKindOptions as commerceKinds } from '@/utils/commerce-products';

type ProductKind =
  | 'pro_plan'
  | 'team_plan'
  | 'team_seat_pack'
  | 'credit_pack'
  | 'storage_pack'
  | 'record_pack'
  | 'component_license'
  | 'component_development_license'
  | 'marketplace_license';
type CredentialStatus = { ready: boolean; missing: string[] };
type Channel = {
  enabled: boolean;
  displayName: string;
  minAmountMinor: number;
  maxAmountMinor: number;
  sort: number;
  maintenanceMessage: string;
  productKinds: ProductKind[];
  providerPaymentType?: 'wxpay' | 'alipay' | 'unionpay';
  credentialStatus?: CredentialStatus | null;
};
type PaymentCredentials = {
  wechatNative: {
    appId: string;
    mchId: string;
    serialNo: string;
    privateKey: string;
    publicKey: string;
    apiV3Key: string;
    hasPrivateKey?: boolean;
    hasPublicKey?: boolean;
    hasApiV3Key?: boolean;
  };
  alipayPage: {
    appId: string;
    privateKey: string;
    publicKey: string;
    hasPrivateKey?: boolean;
    hasPublicKey?: boolean;
  };
  uuwGateway: { apiBaseUrl: string; pid: string; key: string; hasKey?: boolean };
};
type SystemEndpoints = {
  wechatNotifyUrl: string;
  alipayNotifyUrl: string;
  uuwNotifyUrl: string;
  paymentResultUrl: string;
};
type PaymentSettings = {
  version: number;
  channels: { wechatNative: Channel; alipayPage: Channel; uuwGateway: Channel };
  credentials: PaymentCredentials;
  systemEndpoints: SystemEndpoints | null;
  checkout: { orderTimeoutMinutes: number; pendingOrderLimit: number; customerServiceUrl: string };
  refunds: { windowDays: number; retryLimit: number };
};

const defaultChannel = (displayName: string): Channel => ({
  enabled: false,
  displayName,
  minAmountMinor: 1,
  maxAmountMinor: 10_000_000,
  sort: 0,
  maintenanceMessage: '',
  productKinds: [
    'pro_plan',
    'team_plan',
    'team_seat_pack',
    'credit_pack',
    'storage_pack',
    'record_pack',
    'component_license',
    'component_development_license',
    'marketplace_license',
  ],
});
const defaultCredentials = (): PaymentCredentials => ({
  wechatNative: { appId: '', mchId: '', serialNo: '', privateKey: '', publicKey: '', apiV3Key: '' },
  alipayPage: { appId: '', privateKey: '', publicKey: '' },
  uuwGateway: { apiBaseUrl: 'https://pay.uuw.cn', pid: '', key: '' },
});
const activeTab = ref('wechat');
const wechat = reactive<Channel>(defaultChannel('微信支付'));
const alipay = reactive<Channel>(defaultChannel('支付宝'));
const uuw = reactive<Channel>({ ...defaultChannel('优优聚合支付'), providerPaymentType: 'wxpay' });
const credentials = reactive<PaymentCredentials>(defaultCredentials());
const checkout = reactive({
  orderTimeoutMinutes: 30,
  pendingOrderLimit: 3,
  customerServiceUrl: '',
});
const refunds = reactive({ windowDays: 7, retryLimit: 3 });
const systemEndpoints = ref<SystemEndpoints | null>(null);
const version = ref(0);
const loading = ref(false);
const saving = ref(false);
const reason = ref('');
const productKindOptions = [
  { label: '会员与资源商品', options: commerceKinds },
  {
    label: '许可商品',
    options: [
      { value: 'component_license', label: '组件许可' },
      { value: 'component_development_license', label: '组件二次开发授权' },
      { value: 'marketplace_license', label: '能力商店许可' },
    ],
  },
];
const uuwPaymentTypeOptions = [
  { value: 'wxpay', label: '微信支付（wxpay）' },
  { value: 'alipay', label: '支付宝（alipay）' },
  { value: 'unionpay', label: '云闪付（unionpay）' },
];
function assignChannel(
  target: Channel,
  source: Partial<Channel> | undefined,
  fallbackName: string,
) {
  Object.assign(target, defaultChannel(fallbackName), source ?? {}, {
    productKinds: [...(source?.productKinds ?? defaultChannel(fallbackName).productKinds)],
  });
}
function applySettings(settings: PaymentSettings) {
  version.value = settings.version ?? 0;
  systemEndpoints.value = settings.systemEndpoints ?? null;
  assignChannel(wechat, settings.channels?.wechatNative, '微信支付');
  assignChannel(alipay, settings.channels?.alipayPage, '支付宝');
  assignChannel(uuw, settings.channels?.uuwGateway, '优优聚合支付');
  uuw.providerPaymentType ??= 'wxpay';
  Object.assign(
    credentials.wechatNative,
    defaultCredentials().wechatNative,
    settings.credentials?.wechatNative ?? {},
  );
  Object.assign(
    credentials.alipayPage,
    defaultCredentials().alipayPage,
    settings.credentials?.alipayPage ?? {},
  );
  Object.assign(
    credentials.uuwGateway,
    defaultCredentials().uuwGateway,
    settings.credentials?.uuwGateway ?? {},
  );
  Object.assign(
    checkout,
    { orderTimeoutMinutes: 30, pendingOrderLimit: 3, customerServiceUrl: '' },
    settings.checkout ?? {},
  );
  Object.assign(refunds, { windowDays: 7, retryLimit: 3 }, settings.refunds ?? {});
}
function credentialBadge(channel: Channel): 'success' | 'warning' {
  return channel.credentialStatus?.ready ? 'success' : 'warning';
}
function credentialText(channel: Channel) {
  return channel.credentialStatus?.ready
    ? '凭据已就绪'
    : `缺少：${channel.credentialStatus?.missing.join('、') || '商户配置'}`;
}
function secretHint(configured: boolean | undefined, empty: string) {
  return configured ? '已配置，留空则保持不变' : empty;
}
function invalid(): string | null {
  const channels = [wechat, alipay, uuw];
  if (channels.some((channel) => !channel.displayName.trim())) return '请填写所有渠道的展示名称';
  if (channels.some((channel) => channel.maxAmountMinor < channel.minAmountMinor))
    return '最高支付金额不能低于最低支付金额';
  if (channels.some((channel) => !channel.productKinds.length))
    return '每个渠道至少选择一种可售商品类型';
  return null;
}
function channelPayload(channel: Channel) {
  return {
    enabled: channel.enabled,
    displayName: channel.displayName.trim(),
    minAmountMinor: channel.minAmountMinor,
    maxAmountMinor: channel.maxAmountMinor,
    sort: channel.sort,
    maintenanceMessage: channel.maintenanceMessage.trim(),
    productKinds: [...channel.productKinds],
    ...(channel.providerPaymentType ? { providerPaymentType: channel.providerPaymentType } : {}),
  };
}
function credentialsPayload() {
  return {
    wechatNative: {
      appId: credentials.wechatNative.appId.trim(),
      mchId: credentials.wechatNative.mchId.trim(),
      serialNo: credentials.wechatNative.serialNo.trim(),
      privateKey: credentials.wechatNative.privateKey.trim(),
      publicKey: credentials.wechatNative.publicKey.trim(),
      apiV3Key: credentials.wechatNative.apiV3Key.trim(),
    },
    alipayPage: {
      appId: credentials.alipayPage.appId.trim(),
      privateKey: credentials.alipayPage.privateKey.trim(),
      publicKey: credentials.alipayPage.publicKey.trim(),
    },
    uuwGateway: {
      apiBaseUrl: credentials.uuwGateway.apiBaseUrl.trim().replace(/\/$/, ''),
      pid: credentials.uuwGateway.pid.trim(),
      key: credentials.uuwGateway.key.trim(),
    },
  };
}
function payload() {
  return {
    version: version.value,
    channels: {
      wechatNative: channelPayload(wechat),
      alipayPage: channelPayload(alipay),
      uuwGateway: channelPayload(uuw),
    },
    credentials: credentialsPayload(),
    checkout: { ...checkout, customerServiceUrl: checkout.customerServiceUrl.trim() },
    refunds: { ...refunds },
    reason: reason.value.trim(),
  };
}
async function load() {
  loading.value = true;
  try {
    applySettings(await adminApi.get<PaymentSettings>('/admin/commerce/payment-settings'));
  } catch {
    message.error('支付设置加载失败');
  } finally {
    loading.value = false;
  }
}
async function save() {
  const messageText = invalid();
  if (messageText) {
    message.warning(messageText);
    return;
  }
  if (!reason.value.trim()) {
    message.warning('请填写本次变更原因');
    return;
  }
  saving.value = true;
  try {
    applySettings(
      await adminApi.patch<PaymentSettings>('/admin/commerce/payment-settings', payload()),
    );
    reason.value = '';
    message.success('支付设置已保存并立即生效');
  } catch {
    message.error('保存失败，请检查必填商户凭据或刷新后重试');
  } finally {
    saving.value = false;
  }
}
onMounted(() => {
  void load();
});
</script>

<style scoped>
.payment-settings-page {
  max-width: 1040px;
  margin: 0 auto;
  padding: 32px;
}
.payment-settings-page > header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}
.payment-settings-page h1 {
  margin: 0 0 7px;
  color: #303143;
}
.payment-settings-page header span {
  color: #858798;
}
.payment-tabs {
  margin-top: 18px;
}
.form-row,
.rules-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}
.form-row :deep(.ant-input-number) {
  width: 100%;
}
.save-card {
  margin: 18px 0 28px;
}
.version {
  color: #858798;
  font-size: 12px;
}
@media (max-width: 760px) {
  .payment-settings-page {
    padding: 20px 14px;
  }
  .payment-settings-page > header {
    display: block;
  }
  .payment-settings-page > header .ant-btn {
    margin-top: 12px;
  }
  .form-row,
  .rules-grid {
    grid-template-columns: 1fr;
  }
}
</style>
